import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { DatePickerComponent } from 'src/app/utils/date-picker/date-picker.component';
import { AlarmService } from 'src/app/services/alarm.service';
import { InsightsService } from 'src/app/services/insights.service';
import { Utility } from 'src/app/utils/app-utils';
import { Alarm } from 'src/models/alarm.model';

interface severityData {
  label: string;
  value: number;
}

interface rangeData {
  label: string;
  value: string;
}

declare var $: any;

@Component({
  selector: 'app-insights',
  templateUrl: './insights.component.html',
  styleUrls: ['./insights.component.scss']
})
export class InsightsComponent implements OnInit {
  startDate: number;
  endDate: number;
  selectedTab: string = 'overview';
  title: string = 'General';
  severityData: severityData[] = [];
  selectedDateRange: any;
  selectedRange: string = 'week';
  dateRange: { startDate: string, endDate: string } | null = null;
  resetDatePicker: boolean = false;
  overviewMinHeight: number = 0;
  alarmMinHeight: number = 0;
  operatorMinHeight: number = 0;
  timeOfTheDayMinHeight: number = 0;
  private dateRangeSubscription: Subscription;

  siteInsightData = [
    { label: 'All Sites', value: 0 },
    { label: 'Active Sites', value: 0 },
    { label: 'Inactive Sites', value: 0 },
  ];

  rangeData: rangeData[] = [
    {
      label: '--',
      value: '',
    },
    {
      label: 'Day',
      value: 'day',
    },
    {
      label: 'Week',
      value: 'week',
    },
    {
      label: 'Month',
      value: 'month',
    },
  ]

  // Flip logic for widgets
  insightsFlipped = false;
  @Input() selectedAlarm: Alarm;

  constructor(
    public insightsService: InsightsService,
    public alarmService: AlarmService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    await this.handleRangeClick(this.selectedRange);
    await this.getInsights();
    this.listenToDateRangeChanges();
  }

  @ViewChild(DatePickerComponent) datePicker: DatePickerComponent;

  flipInsights() {
    this.insightsFlipped = !this.insightsFlipped;
  }

  onTabSelected(tab: string) {
    this.selectedTab = tab;
    this.title = tab === 'overview' ? 'General'
      : tab === 'alarm' ? 'Alarm'
        : tab === 'operator' ? 'Operator'
          : tab === 'detailed' ? 'Detailed'
            : '';
  }

  async getInsights() {
    let res = await this.insightsService.getSitesInsights();
    if (res && res.success && res.data && res.data.results) {
      this.updateSiteInsightData(res.data.results);
    } else {
      this.resetSiteInsightData();
    }
  }

  listenToDateRangeChanges() {
    this.dateRangeSubscription = this.insightsService.currentDateRange.subscribe(async (dateRange) => {
      this.startDate = dateRange.startDate;
      this.endDate = dateRange.endDate;
      await this.listAlarmsBySeverity(this.startDate, this.endDate);
    });
  }

  async listAlarmsBySeverity(startDate?: number, endDate?: number) {
    const res = await this.insightsService.getAlarmsBySeverity(startDate, endDate);
    if (res && res.success && res.data && res.data.results) {
      this.severityData = res.data.results.map(item => {
        return {
          label: item.severity,
          value: item.currentPeriodAlarmsCount,
        };
      });
    } else {
      this.severityData = [];
    }
  }

  private updateSiteInsightData(data: { connected: number; disconnected: number; total: number }) {
    this.siteInsightData = [
      { label: 'All Sites', value: data.total },
      { label: 'Active Sites', value: data.connected },
      { label: 'Inactive Sites', value: data.disconnected },
    ];
  }

  private resetSiteInsightData() {
    // Set all values to zero if data fetching fails or data format is incorrect
    this.siteInsightData = [
      { label: 'All Sites', value: 0 },
      { label: 'Active Sites', value: 0 },
      { label: 'Inactive Sites', value: 0 },
    ];
  }

  ngAfterViewInit() {
    this.checkScreenWidth();
    this.initializeCarousels();
    this.cdr.detectChanges();
  }

  initializeCarousels(): void {
    if (this.selectedTab === 'overview' && $('#top-sites-alarm').length) {
      $('#top-sites-alarm').carousel({ wrap: true });
    }

    if (this.selectedTab === 'alarm' && $('#false-rate-alarms').length) {
      $('#false-rate-alarms').carousel({ wrap: true });
    }

    if (this.selectedTab === 'operator' && $('#operator-activity-tab').length) {
      $('#operator-activity-tab').carousel({ wrap: true });
    }
  }

  checkScreenWidth() {
    this.overviewMinHeight = window.innerHeight >= 1300 ? 31.2
      : window.innerHeight >= 1080 ? 31.8
        : window.innerHeight >= 1024 ? 30.5
          : window.innerHeight == 905 ? 25.5
            : 33;
    this.alarmMinHeight = window.innerHeight >= 1300 ? 33
      : window.innerHeight >= 1080 ? 34
        : 30.5;
    this.operatorMinHeight = window.innerHeight >= 1300 ? 31
      : window.innerHeight >= 1080 ? 34
        : window.innerHeight >= 1024 ? 33.8
          : window.innerHeight == 905 ? 33.8
            : 35.5;
    this.timeOfTheDayMinHeight = window.innerHeight >= 1300 ? 26.5
      : window.innerHeight >= 1080 ? 27.5
        : window.innerHeight >= 1024 ? 23.5
          : window.innerHeight == 905 ? 20.5
            : window.innerHeight >= 934 ? 29.5
              : 27;
  }

  async handleRangeClick(range: string) {
    if (range === '') {
      this.startDate = undefined;
      this.endDate = undefined;
      this.insightsService.updateDateRange(this.startDate, this.endDate);
      return;
    }

    const now = new Date();
    let startDate: Date;

    switch (range) {
      case 'day':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());
        break;
      default:
        return;
    }

    this.startDate = startDate.getTime();
    this.endDate = now.getTime();
    this.selectedRange = range;
    this.dateRange = null; // Reset date-picker when a range is selected
    if (this.datePicker) {
      this.datePicker.resetDates(); // Reset the date picker
    }
    this.insightsService.updateDateRange(this.startDate, this.endDate);
  }

  onRangeReset() {
    this.selectedRange = '';
  }

  async handleDateRangeSelected(event: { startDate: string, endDate: string }) {
    this.dateRange = event;
    this.selectedRange = ''; // Reset selectedRange when a date is picked
    this.startDate = new Date(event.startDate).getTime();
    this.endDate = new Date(event.endDate).getTime();
    this.selectedRange = '';
    this.insightsService.updateDateRange(this.startDate, this.endDate);
    this.selectedDateRange = event;
  }

  updateDateRange(dateRange: any) {
    this.selectedDateRange = dateRange;
  }

  ngOnDestroy() {
    Utility.Unsubscribe(this.dateRangeSubscription);
  }

}
